import React, { useState, useEffect } from 'react';
import { Menu, X, User, LogOut, Settings, Award, Trophy, MessageCircle, Home, Instagram, Linkedin, Youtube, Mail } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import LoginModal from './LoginModal';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TikTokIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16.6 5.82s.51.5 0 0A4.28 4.28 0 0 1 15.54 3h-3.09v12.4a2.59 2.59 0 0 1-2.59 2.5c-1.42 0-2.6-1.16-2.6-2.6 0-1.72 1.66-3.01 3.37-2.48V7.66a5.66 5.66 0 0 0-1-.08A5.68 5.68 0 0 0 4 13.26 5.68 5.68 0 0 0 9.69 19a5.68 5.68 0 0 0 5.69-5.69V8.83a7.34 7.34 0 0 0 4.33 1.39V7.13s-1.72.08-3.11-1.31z"/>
  </svg>
);

const SOCIALS = [
  { name: 'Instagram', icon: Instagram, url: 'https://www.instagram.com/bimportal' },
  { name: 'LinkedIn', icon: Linkedin, url: 'https://www.linkedin.com/in/alexanderjusti/' },
  { name: 'YouTube', icon: Youtube, url: 'https://www.youtube.com/@Bimtalks' },
  { name: 'TikTok', icon: TikTokIcon, url: 'https://www.tiktok.com/@alexanderjusti303' },
  { name: 'Email', icon: Mail, url: 'mailto:contato@bimportal.com.br' },
];


const Header = ({ onLoginClick }) => {
  const { user, logout, isAuthenticated } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [userProfileId, setUserProfileId] = useState(null);
  const [loginOpen, setLoginOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isAcademy = location.pathname.startsWith('/academy');

  // Abre o modal de login: usa o handler da página se existir, senão o modal interno do Header
  const handleLoginClick = () => {
    if (onLoginClick) onLoginClick();
    else setLoginOpen(true);
  };

  // Fetch user's BIM Connect profile and unread messages count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (!isAuthenticated || !user?.id) return;
      
      try {
        // First get the user's BIM Connect profile
        const profileRes = await axios.get(`${BACKEND_URL}/api/connect/profiles/user/${user.id}`);
        if (profileRes.data.exists && profileRes.data.profile_id) {
          setUserProfileId(profileRes.data.profile_id);
          
          // Then get unread count
          const unreadRes = await axios.get(`${BACKEND_URL}/api/connect/messages/unread-count?profile_id=${profileRes.data.profile_id}`);
          setUnreadCount(unreadRes.data.unread_count || 0);
        }
      } catch (error) {
        // Silently fail - user may not have a BIM Connect profile
      }
    };

    fetchUnreadCount();
    
    // Poll for updates every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated, user]);


  const menuItems = [];

  const handleNavigation = (item) => {
    if (item.isPage) {
      navigate(item.href);
    } else {
      // If on homepage, scroll to section
      if (location.pathname === '/') {
        const element = document.querySelector(item.href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      } else {
        // Navigate to homepage with hash
        navigate('/' + item.href);
      }
    }
    setIsMenuOpen(false);
  };

  const handleLogout = () => {
    logout();
    setIsMenuOpen(false);
  };

  // Check if we're on the homepage
  const isHomePage = location.pathname === '/';

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-md border-b border-red-900/20">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo and Home Button */}
          <div className="flex items-center space-x-2">
            {/* Home Button - only show when not on homepage */}
            {!isHomePage && (
              <button
                onClick={() => navigate('/')}
                className="p-2 bg-red-600/20 hover:bg-red-600/40 rounded-lg transition-all duration-300 group"
                title="Voltar ao Início"
                data-testid="home-button"
              >
                <Home className="w-5 h-5 text-red-400 group-hover:text-red-300" />
              </button>
            )}
            
            {/* Logo */}
            <div 
              className="flex items-center space-x-3 cursor-pointer"
              onClick={() => navigate('/')}
            >
              <img 
                src="/logos/bimportal.png" 
                alt="AJ Academy" 
                className="h-12 w-auto"
              />
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {menuItems.map((item) => (
              <button
                key={item.label}
                onClick={() => handleNavigation(item)}
                className="px-2 py-2 hover:bg-white/10 rounded-lg transition-all duration-300 flex items-center overflow-hidden"
                data-testid={`nav-${item.href.replace('/', '').replace('#', '')}`}
              >
                {item.logo ? (
                  <img 
                    src={item.logo} 
                    alt={item.label} 
                    className="h-5 w-auto object-contain"
                  />
                ) : (
                  <span className="text-gray-300 hover:text-red-500 text-sm font-medium">{item.label}</span>
                )}
              </button>
            ))}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center space-x-3">
            {/* Redes sociais (fora da Academy) */}
            {!isAcademy && (
              <div className="flex items-center space-x-1" data-testid="header-socials">
                {SOCIALS.map((s) => {
                  const Icon = s.icon;
                  return (
                    <a
                      key={s.name}
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={s.name}
                      aria-label={s.name}
                      data-testid={`social-${s.name.toLowerCase()}`}
                      className="w-9 h-9 flex items-center justify-center rounded-full text-gray-300 hover:text-white hover:bg-red-600/80 transition-all duration-300"
                    >
                      <Icon size={18} />
                    </a>
                  );
                })}
              </div>
            )}

            {/* Ranking Button (apenas na Academy) */}
            {isAcademy && (
              <Button
                onClick={() => navigate('/ranking')}
                variant="ghost"
                className="text-yellow-500 hover:text-yellow-400 hover:bg-yellow-500/10"
                title="Ranking de Certificados"
              >
                <Trophy size={18} />
              </Button>
            )}
            
            {isAuthenticated ? (
              <>
                <div className="flex items-center space-x-2 text-white">
                  <User size={20} className="text-red-500" />
                  <span className="text-sm">{user?.name}</span>
                </div>
                
                {/* Messages Button with Badge */}
                {userProfileId && (
                  <Button
                    onClick={() => navigate('/connect/messages')}
                    variant="ghost"
                    className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 relative"
                    title="Mensagens"
                    data-testid="messages-btn"
                  >
                    <MessageCircle size={18} />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </Button>
                )}
                
                <Button
                  onClick={() => navigate('/meus-certificados')}
                  variant="ghost"
                  className="text-yellow-500 hover:text-yellow-400 hover:bg-yellow-500/10"
                  title="Meus Certificados"
                >
                  <Award size={18} />
                </Button>
                <Button
                  onClick={handleLogout}
                  variant="ghost"
                  className="text-white hover:text-red-500 hover:bg-red-500/10"
                >
                  <LogOut size={18} className="mr-2" />
                  Sair
                </Button>
              </>
            ) : (
              <Button
                onClick={handleLoginClick}
                variant="outline"
                className="border-red-500 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                data-testid="header-login-btn"
              >
                <User size={16} className="mr-2" />
                Entrar / Cadastrar
              </Button>
            )}
            {isAcademy && (
              <Button
                onClick={() => navigate('/academy')}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Matricule-se
              </Button>
            )}
            {user?.role === 'admin' && (
              <Button
                onClick={() => navigate('/admin')}
                className="bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center gap-2"
                title="Painel Administrativo"
                data-testid="admin-panel-btn"
              >
                <Settings size={18} />
                <span className="text-sm font-medium">Admin</span>
              </Button>
            )}
          </div>

          {/* Mobile: engrenagem admin sempre visível + menu */}
          <div className="lg:hidden flex items-center gap-1">
            {user?.role === 'admin' && (
              <button
                onClick={() => navigate('/admin')}
                className="p-2 text-white bg-red-600 rounded-lg"
                title="Painel Administrativo"
                data-testid="mobile-admin-gear"
              >
                <Settings size={20} />
              </button>
            )}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 text-white hover:text-red-500 transition-colors"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden py-4 border-t border-red-900/20">
            <nav className="flex flex-col space-y-2">
              {/* Home button in mobile menu */}
              {!isHomePage && (
                <button
                  onClick={() => {
                    navigate('/');
                    setIsMenuOpen(false);
                  }}
                  className="px-4 py-3 text-left hover:bg-red-500/5 transition-colors duration-300 flex items-center text-red-400"
                  data-testid="mobile-home-button"
                >
                  <Home className="w-5 h-5 mr-3" />
                  <span className="font-medium">Voltar ao Início</span>
                </button>
              )}
              
              {menuItems.map((item) => (
                <button
                  key={item.label}
                  onClick={() => handleNavigation(item)}
                  className="px-4 py-3 text-left hover:bg-red-500/5 transition-colors duration-300 flex items-center overflow-hidden"
                >
                  {item.logo ? (
                    <img 
                      src={item.logo} 
                      alt={item.label} 
                      className="h-5 w-auto object-contain"
                    />
                  ) : (
                    <span className="text-gray-300 hover:text-red-500">{item.label}</span>
                  )}
                </button>
              ))}
              <div className="flex flex-col space-y-2 pt-4 px-4">
                {/* Redes sociais (fora da Academy) */}
                {!isAcademy && (
                  <div className="flex items-center justify-center space-x-4 py-2" data-testid="header-socials-mobile">
                    {SOCIALS.map((s) => {
                      const Icon = s.icon;
                      return (
                        <a
                          key={s.name}
                          href={s.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={s.name}
                          aria-label={s.name}
                          className="w-11 h-11 flex items-center justify-center rounded-full text-gray-300 hover:text-white hover:bg-red-600/80 transition-all"
                        >
                          <Icon size={20} />
                        </a>
                      );
                    })}
                  </div>
                )}

                {/* Ranking Button (apenas na Academy) */}
                {isAcademy && (
                  <Button
                    onClick={() => {
                      navigate('/ranking');
                      setIsMenuOpen(false);
                    }}
                    variant="outline"
                    className="w-full border-yellow-600 text-yellow-500 hover:bg-yellow-500/10"
                  >
                    <Trophy size={18} className="mr-2" />
                    Ranking de Certificados
                  </Button>
                )}
                
                {isAuthenticated ? (
                  <>
                    <div className="text-white text-sm mb-2">
                      Olá, {user?.name}
                    </div>
                    <Button
                      onClick={() => {
                        navigate('/meus-certificados');
                        setIsMenuOpen(false);
                      }}
                      variant="outline"
                      className="w-full border-yellow-600 text-yellow-500 hover:bg-yellow-500/10"
                    >
                      <Award size={18} className="mr-2" />
                      Meus Certificados
                    </Button>
                    {user?.role === 'admin' && (
                      <Button
                        onClick={() => {
                          navigate('/admin');
                          setIsMenuOpen(false);
                        }}
                        className="w-full bg-red-600 hover:bg-red-700 text-white"
                      >
                        <Settings size={18} className="mr-2" />
                        Painel Admin
                      </Button>
                    )}
                    <Button
                      onClick={handleLogout}
                      variant="outline"
                      className="w-full border-red-500 text-red-500 hover:bg-red-500/10"
                    >
                      <LogOut size={18} className="mr-2" />
                      Sair
                    </Button>
                  </>
                ) : (
                  <Button
                    onClick={() => {
                      handleLoginClick();
                      setIsMenuOpen(false);
                    }}
                    variant="outline"
                    className="w-full border-red-500 text-red-400 hover:bg-red-500/10"
                    data-testid="mobile-header-login-btn"
                  >
                    <User size={18} className="mr-2" />
                    Entrar / Cadastrar
                  </Button>
                )}
                {isAcademy && (
                  <Button
                    onClick={() => {
                      navigate('/academy');
                      setIsMenuOpen(false);
                    }}
                    className="w-full bg-red-600 hover:bg-red-700 text-white"
                  >
                    Matricule-se
                  </Button>
                )}
              </div>
            </nav>
          </div>
        )}
      </div>

      <LoginModal isOpen={loginOpen} onClose={() => setLoginOpen(false)} />
    </header>
  );
};

export default Header;